/**
 * @author        nico@zumper.com (Nico Rattazzi)
 * @copyright     Copyright (c) 2017, Zumper
 * @description   Library for Tracking Analytics
 */

import { ON_BROWSER, trackGAEvent } from '@zcode/nr-utils'
import { convertQueryStringToParams } from '@zcode/utils'
import QuickLRU from 'quick-lru'
import { logger } from '../../logger'

/**
 * @param {string} category - analytics category, eg. the page the event happened on
 * @param {string} action - the action being tracked
 * @param {string | null | undefined} [label] - eg. the text of a clicked link
 * @param {string=} [value]
 * @param {object=} [fields]
 * @param {boolean} [fields.nonInteraction]
 */
export const trackNonInteractionEvent = (category, action, label, value, fields) => {
  const existingFields = fields || {}
  trackGAEvent(category, action, label, value, { ...existingFields, nonInteraction: true })
}

export const trackPageview = () => {
  // Ensure client calls only
  if (ON_BROWSER && typeof window !== 'undefined') {
    if (window.dataLayer) {
      // Google Tag Manager
      logger.debug('tracking gtm page view:', window.location.pathname)
      window.dataLayer.push({
        event: 'GAPageview',
      })
    } else if (typeof window.ga === 'function') {
      logger.debug('tracking ga page view:', window.location.pathname)
      window.ga('send', 'pageview', window.location.pathname)
    } else {
      logger.warn('failed to track page view due to missing window.ga')
    }
  }
}

export const trackHideMessageSimilar = (target, isMobile) => {
  if (ON_BROWSER && typeof window !== 'undefined') {
    const url = target.url || target.shortUrl
    const evtName = isMobile ? 'HideMessageSimilarMobile' : 'HideMessageSimilar'
    const feedName = target.feedName || 'PRO'
    trackGAEvent(evtName, feedName, url, 1, { nonInteraction: true })
  }
}

/**
 *
 * @param {{evt?: string; num?: number; target: import('@zcode/state-type').Property | import('@zcode/state-type').Listable }} param0
 */
export const trackMessageInitiation = ({ target, num = 1, evt = 'Message' } = {}) => {
  if (ON_BROWSER && typeof window !== 'undefined') {
    let formattedEvtName = evt
    const url = target.url || target.shortUrl
    const feedName = target.feedName || 'PRO'
    // these lines seem unused in production, can we remove them (or use them)?
    const similar =
      typeof window !== 'undefined' ? convertQueryStringToParams(window.location.search).s : ''
    formattedEvtName = similar ? `s.${formattedEvtName}` : formattedEvtName

    trackGAEvent(formattedEvtName, feedName, url, num, { nonInteraction: true })
  }
}

export const trackMessageCompletion = (args = {}) => {
  if (ON_BROWSER && typeof window !== 'undefined') {
    const { target, result, num = 1, evt = 'Message' } = args
    const url = target.url || target.shortUrl
    const feedName = target.feedName || 'PRO'

    if (result === undefined) {
      // dismissed
      trackGAEvent(`${evt}Dismissed`, feedName, url, num, { nonInteraction: true })
    } else if (result === true) {
      // MessageSent
      trackGAEvent(`${evt}Sent`, feedName, url, num, { nonInteraction: true })
    } else if (result === false) {
      // MessageError
      trackGAEvent(`${evt}Error`, feedName, url, num, { nonInteraction: true })
    } else {
      trackGAEvent(`${evt}${result}`, feedName, url, num, { nonInteraction: true })
    }
  }
}

export const trackFieldError = (field, err, isMobile) => {
  if (ON_BROWSER && typeof window !== 'undefined') {
    const evtName = isMobile ? 'MessagingFieldErrorMobile' : 'MessagingFieldError'
    trackGAEvent(evtName, field, err, 1, { nonInteraction: true })
  }
}

export const trackMessageModification = (msg, src = 'm') => {
  if (ON_BROWSER && typeof window !== 'undefined') {
    const len = `${100 * ((msg.length / 100) | 0)}` // multiple of 100 chars
    trackGAEvent('Messaging', `${src}.modified`, len, 1, { nonInteraction: true })
  }
}

export const trackUnchecked = (target) => {
  if (ON_BROWSER && typeof window !== 'undefined') {
    trackGAEvent('Messaging', `$uncheck.${target.feedName}`, target.url, 1, {
      nonInteraction: true,
    })
  }
}

const eventTrackers = new QuickLRU({ maxSize: 250 })
/**
 * event handler, with bound arguments.
 *
 * @param category
 * @param action
 * @param label
 * @param value
 * @param fields
 * @returns {() => void}
 */
export const onEvent = (category, action, label, value, fields) => {
  const key = JSON.stringify([category, action, label, value, fields])
  if (!eventTrackers.has(key)) {
    eventTrackers.set(key, () => trackGAEvent(category, action, label, value, fields))
  }
  return eventTrackers.get(key)
}
